import { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import {
  hasAnyWeeklyHours,
  listOpenStartTimes,
  type PublicProfileDoc,
  type WeeklyAvailability,
} from '@makhe/shared';
import { db } from '@/lib/firebase';
import { cn } from '@/lib/cn';
import { formatBookingWhen } from '@/lib/formatRelative';

function dayLabel(d: Date): string {
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

function timeLabel(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function dateKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** Hours-constrained day + time picker for soft booking. */
export function BookingSlotPicker({
  sellerId,
  durationMinutes,
  valueMs,
  onChange,
}: {
  sellerId: string;
  durationMinutes: number;
  valueMs: number | null;
  onChange: (ms: number | null) => void;
}) {
  const [week, setWeek] = useState<WeeklyAvailability | null>(null);
  const [loading, setLoading] = useState(true);
  const [dayKey, setDayKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getDoc(doc(db, 'publicProfiles', sellerId))
      .then((snap) => {
        if (cancelled) return;
        const data = snap.data() as PublicProfileDoc | undefined;
        setWeek(data?.sellerProfile?.weeklyAvailability ?? null);
      })
      .catch(() => {
        if (!cancelled) setWeek(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sellerId]);

  const starts = useMemo(() => {
    if (!week || !hasAnyWeeklyHours(week)) return [];
    return listOpenStartTimes({ week, durationMinutes, daysAhead: 14 });
  }, [week, durationMinutes]);

  const days = useMemo(() => {
    const map = new Map<string, Date[]>();
    for (const s of starts) {
      const k = dateKey(s);
      const list = map.get(k) ?? [];
      list.push(s);
      map.set(k, list);
    }
    return [...map.entries()].map(([key, times]) => ({
      key,
      label: dayLabel(times[0]!),
      times,
    }));
  }, [starts]);

  useEffect(() => {
    if (days.length === 0) {
      setDayKey(null);
      return;
    }
    if (valueMs) {
      const match = days.find((d) => d.times.some((t) => t.getTime() === valueMs));
      if (match) {
        setDayKey(match.key);
        return;
      }
    }
    setDayKey((prev) => prev ?? days[0]!.key);
  }, [days, valueMs]);

  const timesForDay = days.find((d) => d.key === dayKey)?.times ?? [];

  if (loading) {
    return <p className="text-sm text-makhe-muted">Loading their hours…</p>;
  }

  if (!week || !hasAnyWeeklyHours(week) || days.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-makhe-line bg-makhe-paper p-4 text-sm text-makhe-muted">
        This creator hasn’t published weekly hours yet, so you can’t book right now. Check back
        soon — or save them and try again later.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <p className="text-[11px] uppercase tracking-widest text-makhe-muted">Day</p>
        <div className="mt-2 flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {days.map((d) => (
            <button
              key={d.key}
              type="button"
              onClick={() => {
                setDayKey(d.key);
                onChange(null);
              }}
              className={cn(
                'shrink-0 rounded-full border px-3.5 py-2 text-xs font-medium transition-colors',
                dayKey === d.key
                  ? 'border-makhe-ink bg-makhe-ink text-makhe-bg'
                  : 'border-makhe-line bg-makhe-paper text-makhe-ink hover:border-makhe-ink',
              )}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-[11px] uppercase tracking-widest text-makhe-muted">
          Time · {durationMinutes} min
        </p>
        <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4">
          {timesForDay.map((t) => {
            const ms = t.getTime();
            const active = valueMs === ms;
            return (
              <button
                key={ms}
                type="button"
                onClick={() => onChange(ms)}
                className={cn(
                  'rounded-md border px-2 py-2.5 text-sm transition-colors',
                  active
                    ? 'border-makhe-primary bg-makhe-brand text-makhe-on'
                    : 'border-makhe-line bg-makhe-paper hover:border-makhe-ink',
                )}
              >
                {timeLabel(t)}
              </button>
            );
          })}
        </div>
      </div>

      {valueMs && (
        <p className="text-sm text-makhe-muted">
          Selected · <span className="text-makhe-ink">{formatBookingWhen(new Date(valueMs))}</span>
        </p>
      )}
    </div>
  );
}
